import { __awaiter } from "tslib";
import { nextTick, onBeforeUnmount, onMounted, ref, computed, defineComponent } from 'vue';
import { useObserveElement, NodeType, RowType, EditorHelper, Context, ContractHelper, PlaceholderHelper, } from '@getaccept/editor-lib-new';
import { CommentsHelper } from '@getaccept/lib-shared-new/src/contextual-commenting/helpers/comments.helper';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared-new/src/contextual-commenting/types/create-contextual-comment-button-state';
import DetachedCommentAnchor from '@getaccept/lib-shared-new/src/contextual-commenting/components/DetachedCommentAnchor.vue';
import EditorSideBySideRow from './EditorSideBySideRow.vue';
import EditorRow from './EditorRow.vue';
import EditorRowColumns from './EditorRowColumns.vue';
import EditorRowSlideShow from './EditorRowSlideShow.vue';
export default defineComponent({
    name: 'EditorSection',
    components: {
        DetachedCommentAnchor,
        EditorRow,
        EditorRowColumns,
        EditorSideBySideRow,
        EditorRowSlideShow,
    },
    props: {
        context: { type: String, default: () => Context.Document },
        focusedRequiredId: { type: String, default: '' },
        sectionId: { type: String, required: true },
        rows: { type: Array, default: () => [] },
        mergeValues: {
            type: Object,
            default: () => ({ document: {}, sender: {}, recipients: [], entity: {} }),
        },
        isPdfViewer: { type: Boolean },
        isDsr: { type: Boolean },
        isPreview: { type: Boolean },
        isHidden: { type: Boolean },
        blockWidthClass: {
            type: String,
            default: null,
        },
        pageWidth: { type: Number, default: 0 },
        isBlockToggled: { type: Boolean },
        sender: {
            type: Object,
            default: () => ({}),
        },
        theme: {
            type: Object,
            default: () => ({}),
        },
        recipient: {
            type: Object,
            default: () => ({}),
        },
        recipientInput: { type: Array, required: true },
        updatingInputs: { type: Array, required: true },
        failedInputUpdates: { type: Array, required: true },
        partiallySignedFields: { type: Array, default: () => [] },
        pusherClient: { type: Object, default: undefined },
        comments: { type: Array, default: () => [] },
        selectedCommentId: { type: String, default: '' },
        showContextualComments: { type: Boolean },
        createContextualCommentButtonState: {
            type: String,
            default: CreateContextualCommentButtonState.Enabled,
        },
        currentUser: { type: Object, default: undefined },
        linkedContracts: { type: Array, default: () => [] },
        mappedPlaceholders: { type: Array, default: () => [] },
        allowedToEditAfterPartiallySigned: {
            type: Boolean,
        },
    },
    emits: [
        'create-comment',
        'select-comment',
        'node-click',
        'observe-node-visibility',
        'fetch-new-image',
        'partially-sign',
        'track-video',
        'update-input-value',
    ],
    setup(props, { emit }) {
        const filteredRows = computed(() => props.rows
            .filter(row => [Context.DsrTemplate, Context.DsrResource, Context.DsrPreview].includes(props.context)
            ? true
            : PlaceholderHelper.filterRowForPlaceholders(row))
            .filter(row => ContractHelper.filterRowByContractLinkAccess(row, props.recipient, props.currentUser)));
        const sectionContainer = ref(null);
        const { observe, disconnect } = useObserveElement(emit);
        const initializeIntersectionObserver = () => __awaiter(this, void 0, void 0, function* () {
            yield nextTick();
            if (!sectionContainer.value) {
                return;
            }
            observe(sectionContainer.value);
        });
        const rowIds = computed(() => props.rows.map(row => row.id));
        const commentsWithoutRowId = computed(() => props.comments.filter(comment => { var _a; return !rowIds.value.includes((_a = comment.editorSelection) === null || _a === void 0 ? void 0 : _a.rowId); }));
        onMounted(() => {
            if (!props.isDsr) {
                return;
            }
            initializeIntersectionObserver();
        });
        onBeforeUnmount(() => {
            if (!props.isDsr) {
                return;
            }
            disconnect();
        });
        return {
            commentsWithoutRowId,
            filterCommentsByRow: CommentsHelper.filterCommentsByRow,
            filteredRows,
            initializeIntersectionObserver,
            isPreviousRowSideBySide: EditorHelper.isPreviousRowSideBySide,
            NodeType,
            rowContainsCoverSize: EditorHelper.rowContainsCoverSize,
            RowType,
            sectionContainer,
        };
    },
});
